import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  MatchingRequestCommand,
  MatchingRequestDto,
  MatchingRestoreDto,
  MatchingWindowDto,
} from '../../api/matchingTypes';
import { addDaysToDateKey, clubToday, MAX_DAYS_AHEAD } from '../../domain/matching/matching';
import { matchingActions } from '../slices/matchingSlice';
import type { AppDispatch, RootState } from '../store';

import { matchingService } from './_deps';

const BOARD_PAGE_SIZE = 20;

type ThunkConfig = { state: RootState };

export const fetchMatchingStatus = createAsyncThunk<void, void, ThunkConfig>(
  'matching/fetchStatus',
  async (_, thunkApi) => {
    const { enabled } = await matchingService.getStatus();
    thunkApi.dispatch(matchingActions.setEnabled(enabled));
  },
);

/** Admin only: the server answers with the state that is now in effect for everyone. */
export const updateMatchingStatus = createAsyncThunk<boolean, { enabled: boolean }, ThunkConfig>(
  'matching/updateStatus',
  async (params, thunkApi) => {
    const { enabled } = await matchingService.setStatus(params);
    thunkApi.dispatch(matchingActions.setEnabled(enabled));
    return enabled;
  },
);

export const fetchMatchingMeta = createAsyncThunk<void, void, ThunkConfig>(
  'matching/fetchMeta',
  async (_, thunkApi) => {
    thunkApi.dispatch(matchingActions.setMeta(await matchingService.getMeta()));
  },
);

export const fetchMatchingBoard = createAsyncThunk<void, { page: number }, ThunkConfig>(
  'matching/fetchBoard',
  async ({ page }, thunkApi) => {
    thunkApi.dispatch(matchingActions.setBoardLoading(true));
    try {
      const today = clubToday();
      const res = await matchingService.getBoard({
        from: today,
        to: addDaysToDateKey(today, MAX_DAYS_AHEAD),
        page,
        size: BOARD_PAGE_SIZE,
      });
      thunkApi.dispatch(matchingActions.setBoardPage(res));
    } finally {
      thunkApi.dispatch(matchingActions.setBoardLoading(false));
    }
  },
);

/** Loads the whole seven-day window at once: it is cheap and gives aggregates plus own requests. */
export const fetchMatchingPanel = createAsyncThunk<void, void, ThunkConfig>(
  'matching/fetchPanel',
  async (_, thunkApi) => {
    const today = clubToday();
    const days = await matchingService.getPanel({
      from: today,
      to: addDaysToDateKey(today, MAX_DAYS_AHEAD),
    });
    thunkApi.dispatch(matchingActions.setPanel(days));
  },
);

export const previewMatchingRequest = createAsyncThunk<
  MatchingWindowDto,
  MatchingRequestCommand,
  ThunkConfig
>('matching/preview', async (command) => matchingService.preview({ command }));

async function refresh(dispatch: AppDispatch) {
  await Promise.allSettled([
    dispatch(fetchMatchingPanel()),
    dispatch(fetchMatchingBoard({ page: 0 })),
  ]);
}

export const createMatchingRequest = createAsyncThunk<
  MatchingRequestDto,
  MatchingRequestCommand,
  ThunkConfig
>('matching/create', async (command, thunkApi) => {
  const res = await matchingService.create({ command });
  thunkApi.dispatch(matchingActions.setRequestVersion({ id: res.id, version: res.version }));
  await refresh(thunkApi.dispatch as AppDispatch);
  return res;
});

export const updateMatchingRequest = createAsyncThunk<
  MatchingRequestDto,
  { id: number; version: number; command: MatchingRequestCommand },
  ThunkConfig
>('matching/update', async (params, thunkApi) => {
  const res = await matchingService.update(params);
  thunkApi.dispatch(matchingActions.setRequestVersion({ id: res.id, version: res.version }));
  await refresh(thunkApi.dispatch as AppDispatch);
  return res;
});

export const cancelMatchingRequest = createAsyncThunk<void, { id: number }, ThunkConfig>(
  'matching/cancel',
  async (params, thunkApi) => {
    await matchingService.cancel(params);
    await refresh(thunkApi.dispatch as AppDispatch);
  },
);

export const copyMatchingRequest = createAsyncThunk<MatchingRequestDto, { id: number }, ThunkConfig>(
  'matching/copy',
  async (params, thunkApi) => {
    const res = await matchingService.copy(params);
    await refresh(thunkApi.dispatch as AppDispatch);
    return res;
  },
);

export const restoreMatchingRequest = createAsyncThunk<MatchingRestoreDto, { id: number }, ThunkConfig>(
  'matching/restore',
  async (params, thunkApi) => {
    const res = await matchingService.restore(params);
    await refresh(thunkApi.dispatch as AppDispatch);
    return res;
  },
);

/** One batch request for all given events; ids that are not numeric are ignored. */
export const fetchMatchingEventViews = createAsyncThunk<void, { eventIds: string[] }, ThunkConfig>(
  'matching/fetchEventViews',
  async ({ eventIds }, thunkApi) => {
    const ids = eventIds.map(Number).filter((n) => Number.isInteger(n) && n > 0);
    if (ids.length === 0) return;
    const views = await matchingService.getEventViews({ eventIds: ids });
    thunkApi.dispatch(matchingActions.setEventViews(views));
  },
);
